/**
 * BIN/CUE → ISO converter.
 *
 * Auto-detects sector layout via fingerprint at sector starts:
 *   MODE1/2048: pure data (rare; usually already an ISO)
 *   MODE1/2352: 12-byte sync + 4-byte header + 2048 data + 4 EDC + 8 reserved + 276 ECC
 *   MODE2/2336: 8-byte subheader + 2328 data
 *   MODE2/2352: 12 sync + 4 header + 8 subheader + 2328 data
 *
 * Sync pattern (offset 0): 00 FF FF FF FF FF FF FF FF FF FF 00
 *
 * Cuesheet parser supports: TRACK / INDEX 00/01 / FILE / PREGAP / POSTGAP /
 * REM / FLAGS / ISRC / PERFORMER / TITLE / CATALOG.
 *
 * Audio tracks (MODE AUDIO) are extracted to WAV (44.1 kHz / 16-bit / stereo).
 * Endian: BIN files dump bytes as the device reads (typically little-endian).
 *
 * Surface: window.__DiskImage.BinCue
 *   parseCue(cueText) → { tracks: [...] }
 *   detectSectorLayout(reader, offset) → 'MODE1/2048' | ... | 'AUDIO'
 *   convert(cue, binFiles, opts) → Promise<{ iso: Blob|null, wavs: [{name, blob}], cueText, mode, warnings }>
 *     opts.onProgress({ done, total }) — byte-level progress across all BINs
 *     opts.signal   — AbortSignal; aborting rejects with err.name === 'AbortError'
 *     opts.baseName — stem for the emitted files ("game" → game.iso, game - track02.wav)
 *
 * `iso` is NULL when the disc carries no data track (a plain audio CD). Handing
 * the user a 0-byte image.iso is indistinguishable from a broken tool, so the
 * caller must check for null instead of downloading whatever came back.
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.BinCue) return;

    var SYNC = [0x00, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0x00];

    function _hasSync(b, off) {
        if (off + SYNC.length > b.length) return false;
        for (var i = 0; i < SYNC.length; i++) if (b[off + i] !== SYNC[i]) return false;
        return true;
    }

    function parseCue(cueText) {
        if (!cueText) return { tracks: [], files: [] };
        var lines = String(cueText).replace(/\r\n/g, '\n').split('\n');
        var files = [];
        var currentFile = null;
        var currentTrack = null;
        var catalog = null;
        var globalPerformer = null;
        var globalTitle = null;
        var rems = [];

        function _msf(s) {
            var m = String(s).match(/^(\d+):(\d+):(\d+)$/);
            if (!m) return null;
            return { m: +m[1], s: +m[2], f: +m[3] };
        }
        function _msfToSector(msf) { return ((msf.m * 60) + msf.s) * 75 + msf.f; }

        lines.forEach(function (rawLine) {
            var line = rawLine.trim();
            if (!line) return;
            var quoted = line.match(/^(\S+)\s+(?:"([^"]*)"|(\S+))(?:\s+(.+))?$/);
            var cmd = (quoted && quoted[1] || line.split(/\s+/)[0] || '').toUpperCase();
            var arg = quoted ? (quoted[2] != null ? quoted[2] : quoted[3]) : null;
            var rest = quoted ? quoted[4] : null;

            switch (cmd) {
                case 'CATALOG':
                    catalog = arg;
                    break;
                case 'PERFORMER':
                    if (currentTrack) currentTrack.performer = arg;
                    else globalPerformer = arg;
                    break;
                case 'TITLE':
                    if (currentTrack) currentTrack.title = arg;
                    else globalTitle = arg;
                    break;
                case 'FILE':
                    currentFile = { name: arg, format: (rest || '').toUpperCase() || 'BINARY', tracks: [] };
                    files.push(currentFile);
                    break;
                case 'TRACK':
                    if (!currentFile) {
                        currentFile = { name: '', format: 'BINARY', tracks: [] };
                        files.push(currentFile);
                    }
                    var tparts = line.split(/\s+/);
                    currentTrack = {
                        number: parseInt(tparts[1], 10),
                        type: tparts[2] || 'AUDIO',
                        indices: {},
                        pregap: null,
                        postgap: null,
                        flags: [],
                        isrc: null,
                        title: null,
                        performer: null,
                        file: currentFile
                    };
                    currentFile.tracks.push(currentTrack);
                    break;
                case 'INDEX':
                    if (!currentTrack) break;
                    var iparts = line.split(/\s+/);
                    var num = parseInt(iparts[1], 10);
                    var msf = _msf(iparts[2]);
                    if (msf) currentTrack.indices[num] = { msf: msf, sector: _msfToSector(msf) };
                    break;
                case 'PREGAP':
                    if (currentTrack && arg) {
                        var p = _msf(arg);
                        if (p) currentTrack.pregap = { msf: p, sectors: _msfToSector(p) };
                    }
                    break;
                case 'POSTGAP':
                    if (currentTrack && arg) {
                        var pp = _msf(arg);
                        if (pp) currentTrack.postgap = { msf: pp, sectors: _msfToSector(pp) };
                    }
                    break;
                case 'FLAGS':
                    if (currentTrack) {
                        currentTrack.flags = line.split(/\s+/).slice(1);
                    }
                    break;
                case 'ISRC':
                    if (currentTrack) currentTrack.isrc = arg;
                    break;
                case 'REM':
                    rems.push(line.slice(4));
                    break;
                default:
                    break;
            }
        });

        var allTracks = [];
        files.forEach(function (f) { f.tracks.forEach(function (t) { allTracks.push(t); }); });
        return { files: files, tracks: allTracks, catalog: catalog, performer: globalPerformer, title: globalTitle, rems: rems };
    }

    /**
     * Probe ~16 sectors at @offset to determine layout.
     */
    function detectSectorLayout(reader, offset, hint) {
        offset = offset || 0;
        return reader.bytes(offset, 4 * 2352).then(function (b) {
            // MODE1/2352: sync at every 2352
            var sync2352 = _hasSync(b, 0) && _hasSync(b, 2352);
            if (sync2352) {
                var mode = b[15];
                if (mode === 1) return 'MODE1/2352';
                if (mode === 2) return 'MODE2/2352';
                return 'MODE_X/2352';
            }
            // MODE2/2336: no sync; subheader byte 0..3 = file/channel/submode/coding
            // Heuristic: if sync absent and size hint is multiple of 2336, treat as MODE2/2336
            if (hint === 'MODE2/2336') return 'MODE2/2336';
            return 'MODE1/2048';
        });
    }

    /**
     * Convert a track's BIN range to ISO sectors.
     *
     * MODE_X/2352 = sync pattern present but the mode byte is neither 1 nor 2
     * (scrambled or exotic dump). It is still a 2352-byte grid, so it MUST NOT
     * fall through to the 2048 default — that would read every sector at the
     * wrong offset. Treated as Form 1 (payload at 16) with a warning.
     */
    function _bytesPerSector(layout) {
        return ({ 'MODE1/2048': 2048, 'MODE1/2352': 2352, 'MODE2/2336': 2336, 'MODE2/2352': 2352, 'MODE_X/2352': 2352 })[layout] || 2048;
    }
    function _dataOffsetInSector(layout) {
        return ({ 'MODE1/2048': 0, 'MODE1/2352': 16, 'MODE2/2336': 8, 'MODE2/2352': 24, 'MODE_X/2352': 16 })[layout] || 0;
    }
    function _dataLenInSector(layout) {
        return ({ 'MODE1/2048': 2048, 'MODE1/2352': 2048, 'MODE2/2336': 2048, 'MODE2/2352': 2048, 'MODE_X/2352': 2048 })[layout] || 2048;
    }

    function _isAudio(t) { return String(t && t.type || '').toUpperCase() === 'AUDIO'; }

    /**
     * Sector size of a whole BIN file.
     *
     * `MODE1/2048` is detectSectorLayout's BLIND fallback — the value it returns
     * when no sync pattern was found. CD-DA carries no sync pattern either, so
     * every audio-leading BIN lands there, and 2048 is the one size it can never
     * be: audio is raw 2352-byte CD frames and the cuesheet's INDEX values count
     * exactly those frames. Reading such a file on a 2048 grid shifts every
     * track boundary by 13% and truncates the tail — measured on a 3-track
     * album BIN: track 2 started 30 KB early and each WAV came out 204 844 B
     * instead of 235 244 B. A cue that declares `TRACK … AUDIO` is authoritative
     * here, so it overrides the fallback.
     */
    function _fileSectorSize(layout, tracks) {
        var bps = _bytesPerSector(layout);
        if (bps === 2048 && (tracks || []).some(_isAudio)) return 2352;
        return bps;
    }

    /**
     * Where a track's own payload starts, and where it physically starts on the
     * disc. They differ by the pregap (INDEX 00 … INDEX 01):
     *
     *  • AUDIO — payload starts at INDEX 00 when present. Those frames are real
     *    audio bytes; dropping them is silent data loss.
     *  • DATA  — payload starts at INDEX 01. A data track's pregap is not part
     *    of the ISO9660 filesystem.
     *
     * The boundary that ENDS a track is always the next track's *physical*
     * start (INDEX 00 when present), never its INDEX 01 — otherwise the data
     * track swallows the next track's pregap and the ISO grows by up to 150
     * garbage sectors (measured: 512 000 B where 204 800 B was correct).
     */
    function _physStart(t) {
        if (t.indices[0]) return t.indices[0].sector;
        return t.indices[1] ? t.indices[1].sector : 0;
    }
    function _payloadStart(t) {
        if (_isAudio(t)) return _physStart(t);
        if (t.indices[1]) return t.indices[1].sector;
        return t.indices[0] ? t.indices[0].sector : 0;
    }

    /**
     * Build a WAV blob from raw 16-bit stereo LE PCM (CD-DA).
     * `pcmParts` is a Blob/Uint8Array list; `pcmLen` its total byte length
     * (the header must state it, and Blob parts do not expose a sum).
     */
    function _wavBlob(pcmParts, pcmLen) {
        var hdr = new Uint8Array(44);
        var dv = new DataView(hdr.buffer);
        // RIFF
        hdr[0] = 0x52; hdr[1] = 0x49; hdr[2] = 0x46; hdr[3] = 0x46;
        dv.setUint32(4, 36 + pcmLen, true);
        hdr[8] = 0x57; hdr[9] = 0x41; hdr[10] = 0x56; hdr[11] = 0x45;
        // fmt chunk
        hdr[12] = 0x66; hdr[13] = 0x6d; hdr[14] = 0x74; hdr[15] = 0x20;
        dv.setUint32(16, 16, true);
        dv.setUint16(20, 1, true);   // PCM
        dv.setUint16(22, 2, true);   // stereo
        dv.setUint32(24, 44100, true);
        dv.setUint32(28, 44100 * 2 * 2, true);
        dv.setUint16(32, 4, true);
        dv.setUint16(34, 16, true);
        // data chunk
        hdr[36] = 0x64; hdr[37] = 0x61; hdr[38] = 0x74; hdr[39] = 0x61;
        dv.setUint32(40, pcmLen, true);
        return new Blob([hdr].concat(pcmParts), { type: 'audio/wav' });
    }

    /**
     * Convert cue+bins into ISO + WAVs.
     * binByName: Map<string, File>
     */
    function _abortError() {
        var e = new Error('cancelled');
        e.name = 'AbortError';
        return e;
    }

    function convert(cueText, binByName, opts) {
        opts = opts || {};
        var parsed = parseCue(cueText);
        var warnings = [];
        if (!parsed.files.length) return Promise.reject(new Error('no FILE entries in cue'));

        var base = _stem(opts.baseName || '');
        var isoName = base ? base + '.iso' : 'image.iso';

        // Shared run state: byte counter for progress, abort flag for Cancel.
        // Progress is reported in INPUT bytes — the only figure known up front
        // (the output size depends on layout), and the one that makes a 90 s
        // run legible instead of a frozen "Converting…".
        var ctl = {
            done: 0,
            total: 0,
            signal: opts.signal || null,
            onProgress: typeof opts.onProgress === 'function' ? opts.onProgress : null,
            lastTick: 0
        };
        ctl.aborted = function () { return !!(ctl.signal && ctl.signal.aborted); };
        ctl.tick = function (force) {
            if (!ctl.onProgress) return;
            var now = Date.now();
            if (!force && now - ctl.lastTick < 120) return;
            ctl.lastTick = now;
            try { ctl.onProgress({ done: ctl.done, total: ctl.total }); } catch (_) {}
        };

        var jobs = [];
        parsed.files.forEach(function (f) {
            var bin = binByName.get(f.name) || _findByBasename(binByName, f.name);
            if (!bin) {
                warnings.push('missing BIN file: ' + f.name);
                return;
            }
            ctl.total += bin.size;
            jobs.push({ file: f, bin: bin });
        });
        if (!jobs.length) return Promise.reject(new Error('none of the cue\'s BIN files were provided'));
        ctl.tick(true);

        // Sequential, not Promise.all: a 22-BIN album otherwise runs 22 readers
        // at once, and the progress bar becomes meaningless while peak memory
        // multiplies by the track count.
        var results = [];
        var chain = Promise.resolve();
        jobs.forEach(function (job) {
            chain = chain.then(function () {
                if (ctl.aborted()) throw _abortError();
                return _processFile(job.file, job.bin, warnings, ctl, base).then(function (r) { results.push(r); });
            });
        });

        return chain.then(function () {
            var isoParts = [];
            var wavs = [];
            results.forEach(function (r) {
                if (!r) return;
                if (r.isoParts && r.isoParts.length) isoParts = isoParts.concat(r.isoParts);
                if (r.wavs) wavs = wavs.concat(r.wavs);
            });
            // No data track (plain audio CD) → no ISO at all. See the header note.
            var iso = isoParts.length ? new Blob(isoParts, { type: 'application/octet-stream' }) : null;
            if (!iso && !wavs.length) {
                return Promise.reject(new Error('nothing to convert: no data sectors and no audio tracks were produced'));
            }
            var layouts = results.map(function (r) { return r ? r.layout : null; });
            ctl.done = ctl.total;
            ctl.tick(true);

            return {
                iso: iso,
                isoName: iso ? isoName : null,
                wavs: wavs,
                cueText: _emitCue(parsed, wavs, isoName),
                cueName: base ? base + '.cue' : 'image.cue',
                mode: layouts.filter(Boolean)[0] || null,
                modeBreakdown: layouts,
                warnings: warnings,
                trackCount: parsed.tracks.length,
                wavCount: wavs.length
            };
        });
    }

    /** Filename stem, stripped of extension and of characters no OS likes. */
    function _stem(name) {
        var s = String(name || '').split(/[\\/]/).pop().replace(/\.[A-Za-z0-9]{1,5}$/, '');
        return s.replace(/[<>:"|?*\x00-\x1f]/g, '').replace(/\s+/g, ' ').trim().slice(0, 80);
    }

    function _findByBasename(map, name) {
        var base = String(name).split(/[\\/]/).pop().toLowerCase();
        var hit = null;
        map.forEach(function (v, k) {
            if (String(k).split(/[\\/]/).pop().toLowerCase() === base) hit = v;
        });
        return hit;
    }

    function _processFile(file, bin, warnings, ctl, base) {
        var BinaryReader = w.__DiskImage.BinaryReader;
        var reader = new BinaryReader(bin);
        return detectSectorLayout(reader, 0).then(function (layout) {
            var bps = _fileSectorSize(layout, file.tracks);
            if (layout === 'MODE_X/2352') {
                warnings.push('unknown sector mode in ' + (file.name || bin.name) + ' — read as Form 1');
            }
            var totalSectors = Math.floor(bin.size / bps);
            var isoParts = [];
            var wavs = [];
            var p = Promise.resolve();

            // Sort + slice tracks by PHYSICAL position (INDEX 00 first when present)
            var tracks = file.tracks.slice().sort(function (a, b) { return _physStart(a) - _physStart(b); });

            tracks.forEach(function (t, i) {
                var startSec = _payloadStart(t);
                // Last track runs to EOF: `totalSectors` floors away a partial
                // trailing sector, and for audio that tail is real samples.
                var endByte = (i + 1 < tracks.length)
                    ? _physStart(tracks[i + 1]) * bps
                    : (_isAudio(t) ? bin.size : totalSectors * bps);
                t._sec = { start: startSec, endByte: Math.min(endByte, bin.size) };
            });

            tracks.forEach(function (t) {
                p = p.then(function () {
                    if (ctl.aborted()) throw _abortError();
                    var byteOff = t._sec.start * bps;
                    var byteLen = t._sec.endByte - byteOff;
                    if (byteLen <= 0 || byteOff >= bin.size) return;

                    if (_isAudio(t)) {
                        // Raw CD-DA frames are already PCM 16-bit LE stereo 44.1 kHz.
                        // Keep whole sample frames (4 B) so the WAV never ends mid-frame.
                        byteLen -= byteLen % 4;
                        if (byteLen <= 0) return;
                        var name = (base ? base + ' - ' : '') + 'track' + String(t.number).padStart(2, '0') + '.wav';
                        var pcmLen = byteLen;
                        return _readRange(bin, byteOff, byteLen, ctl).then(function (parts) {
                            wavs.push({ name: name, blob: _wavBlob(parts, pcmLen), track: t.number });
                        });
                    }
                    // Data track — strip sync/header/ECC per layout
                    var sectors = Math.floor(byteLen / bps);
                    if (!sectors) return;
                    return _streamData(bin, byteOff, sectors, bps, _dataOffsetInSector(layout), _dataLenInSector(layout), ctl, function (part) {
                        isoParts.push(part);
                    });
                });
            });

            return p.then(function () {
                return { layout: layout, isoParts: isoParts, wavs: wavs };
            });
        });
    }

    // 4 MB read window. BinaryReader's 2 KB sector cache is built for parsers
    // that hop around a filesystem; a linear cook re-stitches every megabyte
    // out of 512 cached sectors, which is what made a 120 MB image take 17 s.
    // Reading the range straight off the File cut the same image to a fraction
    // of that (see tests/playwright/bin-cue-to-iso-e2e.mjs timing note).
    var READ_WINDOW = 4 * 1024 * 1024;
    // Coalesce every ~24 MB: the parts stay as Blobs (browser-managed, spillable
    // to disk) instead of live Uint8Arrays, so peak JS heap stops tracking the
    // output size on 500 MB+ discs.
    var COALESCE_AT = 24 * 1024 * 1024;

    function _slice(bin, off, len) {
        return bin.slice(off, off + len).arrayBuffer().then(function (ab) { return new Uint8Array(ab); });
    }

    /** Read [off, off+len) as an array of Blob parts, reporting progress. */
    function _readRange(bin, off, len, ctl) {
        var parts = [], pending = [], pendingBytes = 0, done = 0;
        function step() {
            if (done >= len) {
                if (pending.length) parts.push(new Blob(pending));
                return parts;
            }
            if (ctl.aborted()) throw _abortError();
            var take = Math.min(READ_WINDOW, len - done);
            return _slice(bin, off + done, take).then(function (raw) {
                pending.push(raw); pendingBytes += raw.length;
                if (pendingBytes >= COALESCE_AT) { parts.push(new Blob(pending)); pending = []; pendingBytes = 0; }
                done += take; ctl.done += take; ctl.tick();
                return step();
            });
        }
        return Promise.resolve().then(step);
    }

    function _streamData(bin, byteOff, sectors, bps, dataOff, dataLen, ctl, push) {
        var SECTORS_PER_CHUNK = Math.max(1, Math.floor(READ_WINDOW / bps));
        var done = 0, pending = [], pendingBytes = 0;
        function flush() {
            if (!pending.length) return;
            push(new Blob(pending));
            pending = []; pendingBytes = 0;
        }
        function step() {
            if (done >= sectors) { flush(); return; }
            if (ctl.aborted()) throw _abortError();
            var take = Math.min(SECTORS_PER_CHUNK, sectors - done);
            var off = byteOff + done * bps;
            var len = Math.min(take * bps, bin.size - off);
            if (len <= 0) { flush(); return; }
            return _slice(bin, off, len).then(function (raw) {
                var whole = Math.floor(raw.length / bps);
                var out = new Uint8Array(whole * dataLen);
                for (var i = 0; i < whole; i++) {
                    out.set(raw.subarray(i * bps + dataOff, i * bps + dataOff + dataLen), i * dataLen);
                }
                pending.push(out); pendingBytes += out.length;
                if (pendingBytes >= COALESCE_AT) flush();
                done += take; ctl.done += len; ctl.tick();
                return step();
            });
        }
        return Promise.resolve().then(step);
    }

    function _emitCue(parsed, wavs, isoName) {
        // Build a cuesheet that references the emitted ISO + per-track WAVs.
        // The names MUST match what the user actually receives — a cuesheet
        // pointing at a file that was never downloaded is its own bug report.
        var lines = [];
        if (parsed.catalog) lines.push('CATALOG ' + parsed.catalog);
        if (parsed.title) lines.push('TITLE "' + parsed.title + '"');
        if (parsed.performer) lines.push('PERFORMER "' + parsed.performer + '"');
        var dataTracks = parsed.tracks.filter(function (t) { return !_isAudio(t); });
        if (dataTracks.length) {
            lines.push('FILE "' + (isoName || 'image.iso') + '" BINARY');
            dataTracks.forEach(function (t) {
                lines.push('  TRACK ' + String(t.number).padStart(2, '0') + ' MODE1/2048');
                if (t.indices[1]) {
                    var s = t.indices[1].msf;
                    lines.push('    INDEX 01 ' + String(s.m).padStart(2, '0') + ':' + String(s.s).padStart(2, '0') + ':' + String(s.f).padStart(2, '0'));
                }
            });
        }
        wavs.forEach(function (wav) {
            lines.push('FILE "' + wav.name + '" WAVE');
            lines.push('  TRACK ' + String(wav.track).padStart(2, '0') + ' AUDIO');
            lines.push('    INDEX 01 00:00:00');
        });
        return lines.join('\n') + '\n';
    }

    w.__DiskImage.BinCue = {
        parseCue: parseCue,
        detectSectorLayout: detectSectorLayout,
        convert: convert
    };
})(window);
